'use client';

import React, { useState, useEffect } from 'react';
import { useAdManagement } from '@/hooks/useAdManagement';
import { X, ExternalLink } from 'lucide-react';

export default function GlobalStickyFooterAd() {
    const { getActiveAdsByLocation, trackImpression, trackClick, loading } = useAdManagement();
    const [isDismissed, setIsDismissed] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    // Get the active ad for global sticky footer
    const activeAd = React.useMemo(() => {
        const ads = getActiveAdsByLocation('global-sticky-footer');
        return ads.length > 0 ? ads[0] : null;
    }, [getActiveAdsByLocation]);

    // Show the ad after a short delay (better UX)
    useEffect(() => {
        if (activeAd && !isDismissed) {
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 2000); // Show after 2 seconds
            return () => clearTimeout(timer);
        }
    }, [activeAd, isDismissed]);

    // Track impression when ad becomes visible
    useEffect(() => {
        if (activeAd && isVisible) {
            trackImpression(activeAd.id);
        }
    }, [activeAd, isVisible, trackImpression]);

    const handleClick = async () => {
        if (!activeAd) return;

        await trackClick(activeAd.id);

        if (activeAd.buttonUrl) {
            if (activeAd.buttonUrl.startsWith('mailto:')) {
                window.location.href = activeAd.buttonUrl;
            } else {
                window.open(activeAd.buttonUrl, '_blank', 'noopener,noreferrer');
            }
        }
    };

    const handleDismiss = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsDismissed(true);
        setIsVisible(false);
    };

    // Don't render if loading, no ad, dismissed, or not visible yet
    if (loading || !activeAd || isDismissed || !isVisible) {
        return null;
    }

    return (
        <div
            className="fixed bottom-0 left-0 right-0 z-50 transform transition-transform duration-500 ease-out"
            style={{
                transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
            }}
        >
            {/* Gradient shadow above the bar */}
            <div className="h-4 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
            
            <div
                className="relative cursor-pointer"
                onClick={handleClick}
                style={{ backgroundColor: activeAd.backgroundColor }}
            >
                <div className="max-w-7xl mx-auto px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                        {/* Left: Image (optional) + Content */}
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                            {activeAd.imageUrl && (
                                <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-white/10">
                                    <img
                                        src={activeAd.imageUrl}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p
                                    className="font-semibold text-sm sm:text-base truncate"
                                    style={{ color: activeAd.textColor }}
                                >
                                    {activeAd.title}
                                </p>
                                <p
                                    className="text-xs sm:text-sm opacity-80 truncate hidden sm:block"
                                    style={{ color: activeAd.textColor }}
                                >
                                    {activeAd.description}
                                </p>
                            </div>
                        </div>

                        {/* Right: CTA + Close */}
                        <div className="flex items-center gap-3 flex-shrink-0">
                            <button
                                className="px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all duration-200 hover:opacity-90 hover:scale-105 active:scale-95 flex items-center gap-2"
                                style={{
                                    backgroundColor: activeAd.textColor,
                                    color: activeAd.backgroundColor
                                }}
                            >
                                {activeAd.buttonText}
                                <ExternalLink size={14} className="hidden sm:block" />
                            </button>

                            {/* Close Button */}
                            <button
                                onClick={handleDismiss}
                                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                                style={{ color: activeAd.textColor }}
                                aria-label="Dismiss"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Sponsored Badge */}
                        <span
                            className="absolute top-1 left-1/2 -translate-x-1/2 text-[8px] font-medium uppercase tracking-wider opacity-60"
                            style={{ color: activeAd.textColor }}
                        >
                            Sponsored
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

